import { cx } from 'cva';
import { type FunctionComponent, useMemo, useState } from 'react';
import {
  Attachment as DefaultAttachment,
  Avatar as DefaultAvatar,
  MessageBlocked as DefaultMessageBlocked,
  MessageBouncePrompt as DefaultMessageBouncePrompt,
  MessageDeleted as DefaultMessageDeleted,
  MessageIsThreadReplyInChannelButtonIndicator as DefaultMessageIsThreadReplyInChannelButtonIndicator,
  MessageOptions as DefaultMessageOptions,
  MessageRepliesCountButton as DefaultMessageRepliesCountButton,
  MessageStatus as DefaultMessageStatus,
  MessageTimestamp as DefaultMessageTimestamp,
  ReactionsList as DefaultReactionsList,
  ReminderNotification as DefaultReminderNotification,
  StreamedMessageText as DefaultStreamedMessageText,
  isDateSeparatorMessage,
  isMessageBlocked,
  isMessageBounced,
  isMessageEdited,
  MessageBounceModal,
  MessageEditedTimestamp,
  MessageErrorIcon,
  messageHasAttachments,
  messageHasReactions,
  MessageText,
  Poll,
  useChatContext,
  useComponentContext,
  useMessageContext,
  useMessageReminder,
  useTranslationContext,
} from 'stream-chat-react';
import { EditMessageModal as DefaultEditMessageModal } from '../EditMessageModal/EditMessageModal';

export const Message: FunctionComponent = () => {
  const {
    additionalMessageInputProps,
    editing,
    endOfGroup,
    firstOfGroup,
    groupedByUser,
    handleAction,
    handleOpenThread,
    handleRetry,
    highlighted,
    isMessageAIGenerated,
    isMyMessage,
    message,
    onUserClick,
    onUserHover,
    renderText,
    threadList,
  } = useMessageContext('Message');
  const { client } = useChatContext('Message');
  const { t } = useTranslationContext('Message');
  const [isBounceDialogOpen, setIsBounceDialogOpen] = useState(false);
  const [isEditedTimestampOpen, setIsEditedTimestampOpen] = useState(false);

  const reminder = useMessageReminder(message.id);

  const {
    Attachment = DefaultAttachment,
    Avatar = DefaultAvatar,
    EditMessageModal = DefaultEditMessageModal,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- ignore
    MessageOptions = DefaultMessageOptions,
    // TODO: remove this "passthrough" in the next
    // major release and use the new default instead
    MessageActions = MessageOptions,
    MessageBlocked = DefaultMessageBlocked,
    MessageBouncePrompt = DefaultMessageBouncePrompt,
    MessageDeleted = DefaultMessageDeleted,
    MessageIsThreadReplyInChannelButtonIndicator = DefaultMessageIsThreadReplyInChannelButtonIndicator,
    MessageRepliesCountButton = DefaultMessageRepliesCountButton,
    MessageStatus = DefaultMessageStatus,
    MessageTimestamp = DefaultMessageTimestamp,
    PinIndicator,
    ReactionsList = DefaultReactionsList,
    ReminderNotification = DefaultReminderNotification,
    StreamedMessageText = DefaultStreamedMessageText,
  } = useComponentContext('Message');
  const hasAttachment = messageHasAttachments(message);
  const hasReactions = messageHasReactions(message);
  const isAIGenerated = useMemo(
    () => isMessageAIGenerated?.(message),
    [isMessageAIGenerated, message],
  );
  const finalAttachments = useMemo(
    () =>
      !message.shared_location && !message.attachments
        ? []
        : message.shared_location
          ? [message.shared_location, ...(message.attachments ?? [])]
          : message.attachments,
    [message],
  );

  if (isDateSeparatorMessage(message)) {
    return null;
  }

  if (message.deleted_at || message.type === 'deleted') {
    return <MessageDeleted message={message} />;
  }

  if (isMessageBlocked(message)) {
    return <MessageBlocked />;
  }

  const showMetadata = !groupedByUser || endOfGroup;
  const showReplyCountButton = !threadList && !!message.reply_count;
  const showIsReplyInChannel =
    !threadList && message.show_in_channel && message.parent_id;
  const allowRetry =
    message.status === 'failed' && message.error?.status !== 403;
  const isBounced = isMessageBounced(message);
  const isEdited = isMessageEdited(message) && !isAIGenerated;

  let handleClick: (() => void) | undefined;

  if (allowRetry) {
    handleClick = void (async () => {
      await handleRetry(message);
    })();
  } else if (isBounced) {
    handleClick = () => {
      setIsBounceDialogOpen(true);
    };
  } else if (isEdited) {
    handleClick = () => {
      setIsEditedTimestampOpen((prev) => !prev);
    };
  }

  const rootClassName = cx(
    'str-chat__message str-chat__message-simple',
    `str-chat__message--${message.type}`,
    `str-chat__message--${message.status}`,
    isMyMessage()
      ? 'str-chat__message--me str-chat__message-simple--me'
      : 'str-chat__message--other',
    message.text ? 'str-chat__message--has-text' : 'has-no-text',
    {
      'str-chat__message--has-attachment': hasAttachment,
      'str-chat__message--highlighted': highlighted,
      'str-chat__message--pinned pinned-message': message.pinned,
      'str-chat__message--with-reactions': hasReactions,
      'str-chat__message-send-can-be-retried':
        message.status === 'failed' && message.error?.status !== 403,
      'str-chat__message-with-thread-link':
        showReplyCountButton || showIsReplyInChannel,
      'str-chat__virtual-message__wrapper--end': endOfGroup,
      'str-chat__virtual-message__wrapper--first': firstOfGroup,
      'str-chat__virtual-message__wrapper--group': groupedByUser,
    },
  );

  const poll = message.poll_id && client.polls.fromState(message.poll_id);

  return (
    <>
      {editing && (
        <EditMessageModal
          additionalMessageInputProps={additionalMessageInputProps}
        />
      )}
      {isBounceDialogOpen && (
        <MessageBounceModal
          MessageBouncePrompt={MessageBouncePrompt}
          onClose={() => {
            setIsBounceDialogOpen(false);
          }}
          open={isBounceDialogOpen}
        />
      )}
      {
        <div className={rootClassName} key={message.id}>
          {PinIndicator && <PinIndicator />}
          {!!reminder && <ReminderNotification reminder={reminder} />}
          {message.user && (
            <Avatar
              image={message.user.image}
              name={message.user.name ?? message.user.id}
              onClick={onUserClick}
              onMouseOver={onUserHover}
              user={message.user}
            />
          )}
          <div
            className={cx('str-chat__message-inner', {
              'str-chat__simple-message--error-failed': allowRetry || isBounced,
            })}
            data-testid="message-inner"
            onClick={handleClick}
            onKeyUp={handleClick}
          >
            <MessageActions />

            <div className="str-chat__message-bubble">
              {poll && <Poll poll={poll} />}
              {finalAttachments?.length && !message.quoted_message ? (
                <Attachment
                  actionHandler={handleAction}
                  attachments={finalAttachments}
                />
              ) : null}
              {isAIGenerated ? (
                <StreamedMessageText
                  message={message}
                  renderText={renderText}
                />
              ) : (
                <MessageText message={message} renderText={renderText} />
              )}

              <MessageErrorIcon />
            </div>
            <div className="str-chat__message-reactions-host">
              {hasReactions && <ReactionsList reverse />}
            </div>
          </div>
          {showReplyCountButton && (
            <MessageRepliesCountButton
              onClick={handleOpenThread}
              reply_count={message.reply_count}
            />
          )}
          {showIsReplyInChannel && (
            <MessageIsThreadReplyInChannelButtonIndicator />
          )}
          {showMetadata && (
            <div className="str-chat__message-metadata">
              <MessageStatus />
              {!isMyMessage() && !!message.user && (
                <span className="str-chat__message-simple-name">
                  {message.user.name ?? message.user.id}
                </span>
              )}
              <MessageTimestamp customClass="str-chat__message-simple-timestamp" />
              {isEdited && (
                <span className="str-chat__mesage-simple-edited">
                  {t('Edited')}
                </span>
              )}
              {isEdited && (
                <MessageEditedTimestamp calendar open={isEditedTimestampOpen} />
              )}
            </div>
          )}
        </div>
      }
    </>
  );
};
